import React, { useRef, useState } from 'react';
import { COMPLETE_CATEGORY_PDF_SLOT } from '../../constants/documents';

function FileIcon({ mimeType }) {
  const isPdf = mimeType?.includes('pdf');
  const isImg = mimeType?.startsWith('image');
  if (isPdf) return (
    <svg className="w-4 h-4 text-red-500 shrink-0" fill="currentColor" viewBox="0 0 24 24">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm4 18H6V4h7v5h5v11z" />
    </svg>
  );
  if (isImg) return (
    <svg className="w-4 h-4 text-blue-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  );
  return (
    <svg className="w-4 h-4 text-slate-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );
}

function formatBytes(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function CategoryCard({ category, documents, uploading, onUpload, onDelete, onView, onUploadCompletePdf }) {
  const inputRefs = useRef({});
  const extraInputRef = useRef(null);
  const completePdfInputRef = useRef(null);
  const [docName, setDocName] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [addableCount, setAddableCount] = useState(null);

  const isDynamic = category.isDynamic;
  const isAddable = category.isAddable;

  // Separate complete category PDF from slot documents
  const completePdfDoc = (documents || []).find((d) => d.slotName === COMPLETE_CATEGORY_PDF_SLOT);
  const slotDocuments = (documents || []).filter((d) => d.slotName !== COMPLETE_CATEGORY_PDF_SLOT);

  const docsBySlot = {};
  slotDocuments.forEach((d) => {
    docsBySlot[d.slotName] = d;
  });

  // Build slots for addable categories
  let effectiveSlots = category.slots;
  if (isAddable && category.slotPrefix) {
    const uploadedKeys = Object.keys(docsBySlot).filter((k) => k.startsWith(category.slotPrefix));
    const maxUploaded = uploadedKeys.reduce((max, k) => {
      const n = parseInt(k.replace(category.slotPrefix, ''), 10);
      return n > max ? n : max;
    }, 0);
    const count = addableCount !== null ? addableCount : Math.max(maxUploaded, 1);
    if (addableCount === null && count > 0) setAddableCount(count);
    effectiveSlots = [];
    for (let i = 1; i <= count; i++) {
      effectiveSlots.push({ key: `${category.slotPrefix}${i}`, label: `${category.slotLabelPrefix}${i}` });
    }
  }

  const uploadedCount = isDynamic ? slotDocuments.length : effectiveSlots.filter((s) => docsBySlot[s.key]).length;
  const totalSlots = effectiveSlots.length;
  const pct = isDynamic ? (uploadedCount > 0 ? 100 : 0) : Math.round((uploadedCount / totalSlots) * 100);

  const triggerUpload = (slotKey) => {
    if (!inputRefs.current[slotKey]) return;
    inputRefs.current[slotKey].value = '';
    inputRefs.current[slotKey].click();
  };

  const handleFileChange = (slotKey, e) => {
    const file = e.target.files?.[0];
    if (file) onUpload(category.key, slotKey, file);
  };

  const handleExtraUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file || !docName.trim()) return;
    const slotKey = `extra_${Date.now()}_${docName.trim().replace(/\s+/g, '_').toLowerCase()}`;
    onUpload(category.key, slotKey, file, docName.trim());
    setDocName('');
    setShowAddForm(false);
    if (extraInputRef.current) extraInputRef.current.value = '';
  };

  return (
    <div className="card overflow-hidden animate-fade-in">
      {/* Header */}
      <div className="px-5 py-4 border-b border-slate-100">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-slate-800 text-sm">{category.label}</h3>
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
            isDynamic
              ? uploadedCount > 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'
              : uploadedCount === totalSlots
              ? 'bg-emerald-50 text-emerald-700'
              : uploadedCount > 0
              ? 'bg-amber-50 text-amber-700'
              : 'bg-slate-100 text-slate-500'
          }`}>
            {isDynamic ? `${uploadedCount} file${uploadedCount !== 1 ? 's' : ''}` : `${uploadedCount}/${totalSlots}`}
          </span>
        </div>
        {/* Progress bar */}
        <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${
              pct === 100 ? 'bg-emerald-500' : 'bg-brand-500'
            }`}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      {/* Slots */}
      <div className="divide-y divide-slate-50">
        {/* Fixed slots */}
        {effectiveSlots.map((slot) => {
          const doc = docsBySlot[slot.key];
          const isUploading = uploading === `${category.key}:${slot.key}`;

          return (
            <div key={slot.key} className="px-5 py-3 flex items-center gap-3">
              <div className={`w-2 h-2 rounded-full shrink-0 ${doc ? 'bg-emerald-400' : 'bg-slate-200'}`} />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-slate-600">{slot.label}</p>
                {doc && (
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <FileIcon mimeType={doc.mimeType} />
                    <span className="text-xs text-slate-400 truncate max-w-[120px] sm:max-w-[180px]">
                      {doc.originalName}
                    </span>
                    {doc.fileSize && (
                      <span className="text-xs text-slate-300 hidden sm:inline">
                        · {formatBytes(doc.fileSize)}
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {isUploading ? (
                  <div className="flex items-center gap-1.5 text-brand-500">
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                    <span className="text-xs">Uploading…</span>
                  </div>
                ) : (
                  <>
                    {doc && (
                      <>
                        <button onClick={() => onView && onView(doc)} className="p-1.5 rounded-lg text-brand-600 hover:bg-brand-50 transition-colors" title="View">
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </button>
                        <button onClick={() => onDelete(doc._id, slot.label)} className="p-1.5 rounded-lg text-red-400 hover:bg-red-50 hover:text-red-600 transition-colors" title="Delete">
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => triggerUpload(slot.key)}
                      className={`p-1.5 rounded-lg transition-colors ${
                        doc ? 'text-slate-400 hover:bg-slate-50 hover:text-slate-600' : 'text-brand-500 hover:bg-brand-50 hover:text-brand-700'
                      }`}
                      title={doc ? 'Replace' : 'Upload'}
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                      </svg>
                    </button>
                    <input
                      ref={(el) => (inputRefs.current[slot.key] = el)}
                      type="file"
                      className="hidden"
                      accept=".pdf,.jpg,.jpeg,.png,.webp"
                      onChange={(e) => handleFileChange(slot.key, e)}
                    />
                  </>
                )}
              </div>
            </div>
          );
        })}

        {/* Add New button for addable categories */}
        {isAddable && (
          <div className="px-5 py-2 border-t border-slate-100">
            <button
              onClick={() => setAddableCount((prev) => (prev || 1) + 1)}
              className="flex items-center gap-2 text-xs font-medium text-brand-600 hover:text-brand-700"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
              </svg>
              Add New Semester Memo
            </button>
          </div>
        )}

        {/* Dynamic extra documents */}
        {isDynamic && slotDocuments.map((doc) => (
          <div key={doc._id} className="px-5 py-3 flex items-center gap-3">
            <div className="w-2 h-2 rounded-full shrink-0 bg-emerald-400" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-slate-600">{doc.slotName || 'Untitled'}</p>
              <div className="flex items-center gap-1.5 mt-0.5">
                <FileIcon mimeType={doc.mimeType} />
                <span className="text-xs text-slate-400 truncate max-w-[120px] sm:max-w-[180px]">
                  {doc.originalName}
                </span>
                {doc.fileSize && (
                  <span className="text-xs text-slate-300 hidden sm:inline">
                    · {formatBytes(doc.fileSize)}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <button onClick={() => onView && onView(doc)} className="p-1.5 rounded-lg text-brand-600 hover:bg-brand-50 transition-colors" title="View">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </button>
              <button onClick={() => onDelete(doc._id, doc.slotName || 'Extra document')} className="p-1.5 rounded-lg text-red-400 hover:bg-red-50 hover:text-red-600 transition-colors" title="Delete">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          </div>
        ))}

        {/* Add extra document form */}
        {isDynamic && (
          <div className="px-5 py-3">
            {showAddForm ? (
              <div className="space-y-2">
                <input
                  type="text"
                  value={docName}
                  onChange={(e) => setDocName(e.target.value)}
                  placeholder="Document name (e.g. Visa Copy)"
                  maxLength={100}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => { setShowAddForm(false); setDocName(''); }}
                    className="px-3 py-1.5 text-xs font-medium text-slate-500 border border-slate-200 rounded-lg hover:bg-slate-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => docName.trim() && extraInputRef.current?.click()}
                    disabled={!docName.trim()}
                    className="px-3 py-1.5 text-xs font-medium text-white bg-brand-500 rounded-lg hover:bg-brand-600 disabled:opacity-50"
                  >
                    Choose File
                  </button>
                  <input
                    ref={extraInputRef}
                    type="file"
                    className="hidden"
                    accept=".pdf,.jpg,.jpeg,.png,.webp"
                    onChange={handleExtraUpload}
                  />
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowAddForm(true)}
                className="flex items-center gap-2 text-xs font-medium text-brand-600 hover:text-brand-700"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                </svg>
                Add Extra Document
              </button>
            )}
          </div>
        )}
      </div>

      {/* Complete Category PDF section */}
      <div className="px-5 py-3 border-t border-dashed border-slate-200">
        <p className="text-[10px] text-slate-400 text-center mb-2">
          Or upload a single PDF for all {category.label} documents
        </p>

        {completePdfDoc ? (
          <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2.5">
            <svg className="w-5 h-5 text-red-500 shrink-0" fill="currentColor" viewBox="0 0 24 24">
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm4 18H6V4h7v5h5v11z" />
            </svg>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-slate-700">Complete {category.label} PDF</p>
              <p className="text-[10px] text-slate-400 truncate">{completePdfDoc.originalName}</p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button onClick={() => onView && onView(completePdfDoc)} className="p-1.5 rounded-lg text-brand-600 hover:bg-brand-50 transition-colors" title="View">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </button>
              <button onClick={() => completePdfInputRef.current?.click()} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-50 hover:text-slate-600 transition-colors" title="Replace">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
              </button>
              <button onClick={() => onDelete(completePdfDoc._id, `Complete ${category.label} PDF`)} className="p-1.5 rounded-lg text-red-400 hover:bg-red-50 hover:text-red-600 transition-colors" title="Delete">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          </div>
        ) : uploading === `${category.key}:${COMPLETE_CATEGORY_PDF_SLOT}` ? (
          <div className="flex items-center justify-center gap-2 py-2 text-brand-500">
            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
            <span className="text-xs">Uploading PDF...</span>
          </div>
        ) : (
          <div className="text-center">
            <button
              onClick={() => completePdfInputRef.current?.click()}
              className="inline-flex items-center gap-2 px-4 py-2 text-xs font-medium text-brand-600 bg-brand-50 border border-brand-200 rounded-lg hover:bg-brand-100 transition-colors"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm4 18H6V4h7v5h5v11z" />
              </svg>
              Upload Complete PDF
            </button>
          </div>
        )}

        <input
          ref={completePdfInputRef}
          type="file"
          className="hidden"
          accept=".pdf"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file && onUploadCompletePdf) onUploadCompletePdf(category.key, file);
            if (completePdfInputRef.current) completePdfInputRef.current.value = '';
          }}
        />
      </div>
    </div>
  );
}
